/**
 * School Address Fieldset Component
 *
 * Reusable address field group for school and delivery addresses.
 * Uses shadcn Form components with react-hook-form integration.
 *
 * Features:
 * - 12 address fields (id + county dropdown + 10 text inputs)
 * - Integrates with parent form via useFormContext
 * - Dynamic field name prefix (adresa or adresaLivrare)
 * - Design system utilities from main.css
 * - County dropdown with Romanian counties
 *
 * Usage:
 * ```tsx
 * <Form {...form}>
 *   <SchoolAddressFieldset namePrefix="adresa" />
 *   <SchoolAddressFieldset namePrefix="adresaLivrare" disabled={sameAsSchool} />
 * </Form>
 * ```
 *
 * Created: 2025-11-02
 * Version: 1.0
 */

import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCounties } from '@/shared/hooks/use-counties';
import { rezolvaJudet } from '@/domains/schools/validation/rezolva-judet';

/* =============================================================================
   TYPES
   ============================================================================= */

interface SchoolAddressFieldsetProps {
  /**
   * Field name prefix for nested address object.
   * Examples: "adresa", "adresaLivrare", "pickupAddress"
   */
  namePrefix: string;

  /**
   * Disable all fields in the fieldset.
   * Useful when "same as school address" is checked.
   */
  disabled?: boolean;

  /**
   * Ascunde bloc / scara / etaj / apartament. Implicit `false`: comportamentul de pana acum.
   *
   * Il pun DOUA ecrane, amandoua pe adresa SCOLII (o scoala n-are apartament): cererea de
   * inscriere (decizia owner-ului din 18.09) si detaliile scolii (20.09). `SchoolRequestFormPage`
   * si `SchoolDetailsFormPage` il trec pe `namePrefix="adresa"`, nu pe adresa de livrare — acolo
   * cele patru campuri raman, fiindca o livrare chiar poate fi intr-un bloc. Comanda de colectare
   * nu-l pune deloc.
   */
  ascundeDetaliiBloc?: boolean;
}

/* =============================================================================
   COMPONENT
   ============================================================================= */

export function SchoolAddressFieldset({
  namePrefix,
  disabled = false,
  ascundeDetaliiBloc = false,
}: SchoolAddressFieldsetProps) {
  const { control, setValue } = useFormContext();
  const { data: counties, isLoading: loadingCounties, isError: countiesError, refetch: refetchCounties } = useCounties();

  // Build field name for react-hook-form
  const fieldName = (field: string) => `${namePrefix}.${field}` as const;

  // `rezolvaJudet` in Select-ul de mai jos repara doar AFISAREA. Fara scrierea de aici, valoarea
  // din formular ramane cea nenormalizata ("Bucuresti") si pleaca asa la submit -- Zod trece
  // (non-gol), iar backendul o rescrie identic, pana ajunge fix-ul de normalizare al lui F1.
  // O singura scriere, `shouldDirty: false`: dupa ea `rezolvaJudet(canonic, counties)` intoarce
  // chiar `canonic` (prima ramura, potrivire exacta), deci nu mai retrigger-uieste efectul.
  const judetCurent = useWatch({ control, name: fieldName('judet') });
  useEffect(() => {
    const canonic = rezolvaJudet(judetCurent, counties);
    if (canonic && canonic !== judetCurent) {
      setValue(fieldName('judet'), canonic, { shouldDirty: false });
    }
  }, [judetCurent, counties, namePrefix, setValue]);

  return (
    <div className="space-y-4">
      {/* County Dropdown (Required) */}
      {/* `FormItem` invelie tot randul (eticheta + camp), nu doar campul -- altfel `FormLabel`
          citeste un `FormItemContext` gol (e sora, nu fiica), si `htmlFor` nu mai corespunde cu
          id-ul real (S1, tabelul p2, randul p2-6; acelasi tipar ca #177 si constatarea 41). */}
      <FormField
        control={control}
        name={fieldName('judet')}
        render={({ field }) => (
          <div className="space-y-2">
            <FormItem className="form-field-row">
              <FormLabel className="form-label-bold">Județ: *</FormLabel>
              <div className="flex-1 space-y-0">
                <Select
                  onValueChange={field.onChange}
                  // Potrivire TOLERANTA: `judet` poate ajunge din backend nenormalizat
                  // (`Bucuresti` in loc de `BUCURESTI`) — o comparatie stricta ar arata
                  // placeholder-ul desi campul chiar are o valoare. Nesuprapunerea (cod de
                  // abreviere, judet necunoscut, gol) ramane pe placeholder — control negativ,
                  // neschimbat. Vezi `rezolvaJudet`.
                  value={rezolvaJudet(field.value, counties) ?? field.value}
                  disabled={disabled || loadingCounties}
                >
                  <FormControl>
                    <SelectTrigger className="form-select-trigger w-full" aria-required="true">
                      <SelectValue placeholder={loadingCounties ? 'Se încarcă...' : 'Selectați județul'} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="form-select-content">
                    {counties?.map((county: string) => (
                      <SelectItem key={county} value={county}>
                        {county}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Un 500/retea pe lista de judete NU mai lasa dropdown-ul gol,
                    tacut (precedent #191/p3-1) - mesajul si reincercarea stau
                    langa camp, nu inlocuiesc restul formularului: celelalte
                    campuri (needitate de interogarea asta) raman utilizabile. */}
                {countiesError && (
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-error">
                    <span>Lista de județe nu s-a putut încărca.</span>
                    {/* `touch-target-link` (utility existenta in main.css, nu adaugata
                        aici): fara ea, tinta masura 71x20 px la 1280 (S1) — sub minimul
                        WCAG 2.5.8. E gandita exact pentru un buton stilizat ca link. */}
                    <button
                      type="button"
                      onClick={() => refetchCounties()}
                      className="touch-target-link inline-flex items-center underline"
                    >
                      Reîncearcă
                    </button>
                  </div>
                )}
                <FormMessage className="form-message-spacer" />
              </div>
            </FormItem>
          </div>
        )}
      />

      {/* City/Town (Required) */}
      <FormField
        control={control}
        name={fieldName('oras')}
        render={({ field }) => (
          <div className="space-y-2">
            <FormItem className="form-field-row">
              <FormLabel className="form-label-bold">Oraș: *</FormLabel>
              <div className="flex-1 space-y-0">
                <FormControl>
                  <Input
                    {...field}
                    value={field.value || ''}
                    type="text"
                    placeholder="Introduceți orașul"
                    disabled={disabled}
                    aria-required="true"
                    className="form-input-styled"
                  />
                </FormControl>
                <FormMessage className="form-message-spacer" />
              </div>
            </FormItem>
          </div>
        )}
      />

      {/* Municipality (Optional) */}
      <FormField
        control={control}
        name={fieldName('comuna')}
        render={({ field }) => (
          <div className="space-y-2">
            <FormItem className="form-field-row">
              <FormLabel className="form-label-bold">Comună:</FormLabel>
              <div className="flex-1 space-y-0">
                <FormControl>
                  <Input
                    {...field}
                    value={field.value || ''}
                    type="text"
                    placeholder="Opțional"
                    disabled={disabled}
                    className="form-input-styled"
                  />
                </FormControl>
                <FormMessage className="form-message-spacer" />
              </div>
            </FormItem>
          </div>
        )}
      />

      {/* Village (Optional) */}
      <FormField
        control={control}
        name={fieldName('sat')}
        render={({ field }) => (
          <div className="space-y-2">
            <FormItem className="form-field-row">
              <FormLabel className="form-label-bold">Sat:</FormLabel>
              <div className="flex-1 space-y-0">
                <FormControl>
                  <Input
                    {...field}
                    value={field.value || ''}
                    type="text"
                    placeholder="Opțional"
                    disabled={disabled}
                    className="form-input-styled"
                  />
                </FormControl>
                <FormMessage className="form-message-spacer" />
              </div>
            </FormItem>
          </div>
        )}
      />

      {/* Street (Optional) */}
      <FormField
        control={control}
        name={fieldName('strada')}
        render={({ field }) => (
          <div className="space-y-2">
            <FormItem className="form-field-row">
              <FormLabel className="form-label-bold">Stradă:</FormLabel>
              <div className="flex-1 space-y-0">
                <FormControl>
                  <Input
                    {...field}
                    value={field.value || ''}
                    type="text"
                    placeholder="Opțional"
                    disabled={disabled}
                    className="form-input-styled"
                  />
                </FormControl>
                <FormMessage className="form-message-spacer" />
              </div>
            </FormItem>
          </div>
        )}
      />

      {/* Street Number (Optional) */}
      <FormField
        control={control}
        name={fieldName('numar')}
        render={({ field }) => (
          <div className="space-y-2">
            <FormItem className="form-field-row">
              <FormLabel className="form-label-bold">Număr:</FormLabel>
              <div className="flex-1 space-y-0">
                <FormControl>
                  <Input
                    {...field}
                    value={field.value || ''}
                    type="text"
                    placeholder="Opțional"
                    disabled={disabled}
                    className="form-input-styled"
                  />
                </FormControl>
                <FormMessage className="form-message-spacer" />
              </div>
            </FormItem>
          </div>
        )}
      />

      {/* Postal Code (Optional) */}
      <FormField
        control={control}
        name={fieldName('codPostal')}
        render={({ field }) => (
          <div className="space-y-2">
            <FormItem className="form-field-row">
              <FormLabel className="form-label-bold">Cod poștal:</FormLabel>
              <div className="flex-1 space-y-0">
                <FormControl>
                  <Input
                    {...field}
                    value={field.value || ''}
                    type="text"
                    placeholder="Opțional"
                    disabled={disabled}
                    className="form-input-styled"
                  />
                </FormControl>
                <FormMessage className="form-message-spacer" />
              </div>
            </FormItem>
          </div>
        )}
      />

      {/* Bloc / scara / etaj / apartament: doar pe cererea de inscriere a scolii sunt ascunse
          (decizie owner, 18.09 — o scoala nu are apartament). Pe comanda de colectare ramane
          ca pana acum: acolo adresa poate fi un bloc. Campurile raman in starea formularului
          chiar cand nu se randeaza, ca o cerere veche sa nu piarda tacut valorile. */}
      {!ascundeDetaliiBloc && (
        <>
          {/* Building Block (Optional) */}
          <FormField
            control={control}
            name={fieldName('bloc')}
            render={({ field }) => (
              <div className="space-y-2">
                <FormItem className="form-field-row">
                  <FormLabel className="form-label-bold">Bloc:</FormLabel>
                  <div className="flex-1 space-y-0">
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        type="text"
                        placeholder="Opțional"
                        disabled={disabled}
                        className="form-input-styled"
                      />
                    </FormControl>
                    <FormMessage className="form-message-spacer" />
                  </div>
                </FormItem>
              </div>
            )}
          />

          {/* Building Entrance (Optional) */}
          <FormField
            control={control}
            name={fieldName('scara')}
            render={({ field }) => (
              <div className="space-y-2">
                <FormItem className="form-field-row">
                  <FormLabel className="form-label-bold">Scară:</FormLabel>
                  <div className="flex-1 space-y-0">
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        type="text"
                        placeholder="Opțional"
                        disabled={disabled}
                        className="form-input-styled"
                      />
                    </FormControl>
                    <FormMessage className="form-message-spacer" />
                  </div>
                </FormItem>
              </div>
            )}
          />

          {/* Floor (Optional) */}
          <FormField
            control={control}
            name={fieldName('etaj')}
            render={({ field }) => (
              <div className="space-y-2">
                <FormItem className="form-field-row">
                  <FormLabel className="form-label-bold">Etaj:</FormLabel>
                  <div className="flex-1 space-y-0">
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        type="text"
                        placeholder="Opțional"
                        disabled={disabled}
                        className="form-input-styled"
                      />
                    </FormControl>
                    <FormMessage className="form-message-spacer" />
                  </div>
                </FormItem>
              </div>
            )}
          />

          {/* Apartment (Optional) */}
          <FormField
            control={control}
            name={fieldName('apartament')}
            render={({ field }) => (
              <div className="space-y-2">
                <FormItem className="form-field-row">
                  <FormLabel className="form-label-bold">Apartament:</FormLabel>
                  <div className="flex-1 space-y-0">
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        type="text"
                        placeholder="Opțional"
                        disabled={disabled}
                        className="form-input-styled"
                      />
                    </FormControl>
                    <FormMessage className="form-message-spacer" />
                  </div>
                </FormItem>
              </div>
            )}
          />
        </>
      )}
    </div>
  );
}
